import React, { useState, useEffect, useMemo } from "react";
import "./OvertimeApproval.css";
import {
  getOvertimeReports,
  updateOvertimeStatus,
} from "../../../api/reportsApi";
import Preloader from "../../Preloader/Preloader";
import { usePendingReports } from "../../../contexts/PendingReportsContext";

function OvertimeApproval() {
  const { updatePendingCounts } = usePendingReports();
  const [overtimeReports, setOvertimeReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingStatuses, setSavingStatuses] = useState({});
  const [showPopup, setShowPopup] = useState(false);

  const fetchOvertimeReports = async () => {
    try {
      const reports = await getOvertimeReports();
      const formattedReports =
        reports?.overtimes?.map((report) => {
          // Определяем статус на основе существующих согласований
          let currentStatus = null;
          if (report.status) {
            const statuses = Object.values(report.status);
            const hasRejected = statuses.some((status) =>
              typeof status === "object"
                ? status.is_approved === "Не согласован"
                : status === "Не согласован"
            );
            const allApproved = statuses.every((status) =>
              typeof status === "object"
                ? status.is_approved === "Согласован"
                : status === "Согласован"
            );

            if (hasRejected) currentStatus = false;
            else if (allApproved) currentStatus = true;
          }

          return {
            id: report.id,
            userName: `${report.surname} ${report.name} ${report.patronymic}`,
            date: new Date(report.fact_date_start).toLocaleDateString("ru-RU"),
            project: report.project_name,
            hours: `${Math.floor(report.time_spent / 3600)}ч ${Math.floor(
              (report.time_spent % 3600) / 60
            )}м`,
            isApproved: currentStatus,
            group: report.group,
            role: report.role,
          };
        }) || [];
      setOvertimeReports(formattedReports);
    } catch (err) {
      setError("Ошибка при загрузке отчетов о переработках");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOvertimeReports();
  }, []);

  const groupedReports = useMemo(() => {
    const groups = {};
    overtimeReports.forEach((report) => {
      if (!groups[report.group]) {
        groups[report.group] = {};
      }
      if (!groups[report.group][report.role]) {
        groups[report.group][report.role] = {};
      }
      if (!groups[report.group][report.role][report.userName]) {
        groups[report.group][report.role][report.userName] = [];
      }
      groups[report.group][report.role][report.userName].push(report);
    });
    return groups;
  }, [overtimeReports]);

  const handleApprove = (reportId) => {
    setOvertimeReports((reports) =>
      reports.map((report) =>
        report.id === reportId
          ? { ...report, isApproved: report.isApproved === true ? null : true }
          : report
      )
    );
  };

  const handleReject = (reportId) => {
    setOvertimeReports((reports) =>
      reports.map((report) =>
        report.id === reportId
          ? {
              ...report,
              isApproved: report.isApproved === false ? null : false,
            }
          : report
      )
    );
  };

  const handleSave = async () => {
    const reportsToUpdate = overtimeReports.filter(
      (report) => report.isApproved !== null
    );

    try {
      for (const report of reportsToUpdate) {
        setSavingStatuses((prev) => ({ ...prev, [report.id]: "pending" }));
        await updateOvertimeStatus(report.id, report.isApproved);
        setSavingStatuses((prev) => ({ ...prev, [report.id]: "success" }));

      }

      setShowPopup(true);
      setTimeout(async () => {
        await updatePendingCounts();
        setShowPopup(false);
        setIsLoading(true);
        fetchOvertimeReports();
      }, 1000);
    } catch (error) {
      console.error("Ошибка при сохранении статусов:", error);
      setError("Произошла ошибка при сохранении статусов");
    }
  };

  const handleReset = () => {
    setOvertimeReports((reports) =>
      reports.map((report) => ({ ...report, isApproved: null }))
    );
    setSavingStatuses({});
  };

  if (isLoading) return <Preloader />;

  const hasReports = Object.keys(groupedReports).length > 0;

  return (
    <div className="overtime-approval">
      <h2 className="overtime-approval__title">Согласование переработок</h2>
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup">
            <div className="popup-icon"></div>
            <p className="popup-text">Статусы переработок успешно сохранены</p>
          </div>
        </div>
      )}
      {!hasReports ? (
        <div className="overtime-approval__empty">
          Отчеты для согласования отсутствуют
        </div>
      ) : (
        <>
          <div className="overtime-approval__content">
            {Object.entries(groupedReports).map(([group, roles]) => (
              <div key={group} className="overtime-approval__group">
                <h3 className="overtime-approval__group-title">{group}</h3>
                {Object.entries(roles).map(([role, users]) => (
                  <div key={role} className="overtime-approval__role">
                    <h4 className="overtime-approval__role-title">{role}</h4>
                    {Object.entries(users).map(([userName, reports]) => (
                      <div key={userName} className="overtime-approval__user">
                        <h5 className="overtime-approval__user-title">
                          {userName}
                        </h5>
                        <table className="overtime-approval__table">
                          <thead>
                            <tr>
                              <th>Дата</th>
                              <th>Проект</th>
                              <th>Часы</th>
                              <th>Подтверждение</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reports.map((report) => (
                              <tr key={report.id}>
                                <td>{report.date}</td>
                                <td>{report.project}</td>
                                <td>{report.hours}</td>
                                <td className="overtime-approval__actions">
                                  <div className="overtime-approval__status-wrapper">
                                    <button
                                      className={`overtime-approval__action-btn ${
                                        report.isApproved === true
                                          ? "active"
                                          : ""
                                      } ${
                                        savingStatuses[report.id] === "pending"
                                          ? "loading"
                                          : ""
                                      }`}
                                      onClick={() => handleApprove(report.id)}
                                      disabled={
                                        savingStatuses[report.id] === "pending"
                                      }
                                    >
                                      <span className="overtime-approval__check">
                                        ✓
                                      </span>
                                    </button>
                                    <button
                                      className={`overtime-approval__action-btn ${
                                        report.isApproved === false
                                          ? "active"
                                          : ""
                                      } ${
                                        savingStatuses[report.id] === "pending"
                                          ? "loading"
                                          : ""
                                      }`}
                                      onClick={() => handleReject(report.id)}
                                      disabled={
                                        savingStatuses[report.id] === "pending"
                                      }
                                    >
                                      <span className="overtime-approval__cross">
                                        ✕
                                      </span>
                                    </button>
                                    {savingStatuses[report.id] === "error" && (
                                      <span
                                        className="overtime-approval__error-icon"
                                        title="Ошибка сохранения"
                                      >
                                        ⚠️
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="overtime-approval__buttons">
            <button className="button button_type_primary" onClick={handleSave}>
              Сохранить
            </button>
            <button
              className="button button_type_secondary"
              onClick={handleReset}
            >
              Сбросить
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default OvertimeApproval;
